import { notify } from '@servepod/ui';

/**
 * The API client.
 *
 * Thin by design. Its one real job beyond `fetch` is turning the platform's error
 * envelope into a typed `ApiError`, so every call site can branch on a stable `code`
 * instead of matching on prose that changes with copy edits.
 */

export interface ApiErrorBody {
  error: { code: string; message: string; request_id: string; details: Record<string, unknown> };
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly requestId: string;
  readonly details: Record<string, unknown>;

  constructor(status: number, body: ApiErrorBody) {
    super(body.error.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.error.code;
    this.requestId = body.error.request_id;
    this.details = body.error.details;
  }

  /** Whether retrying the identical request could plausibly succeed. */
  get retryable(): boolean {
    return this.status >= 500 || this.code === 'RATE_LIMITED';
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Suppresses the automatic error toast when the caller renders the error itself. */
  quiet?: boolean;
  signal?: AbortSignal;
}

/** Set by the auth store on 401 so the app can bounce to sign-in exactly once. */
let onUnauthenticated: (() => void) | undefined;

export function setUnauthenticatedHandler(handler: () => void): void {
  onUnauthenticated = handler;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      // The session is a first-party cookie; without this it is never sent.
      credentials: 'same-origin',
      headers: {
        accept: 'application/json',
        ...(options.body !== undefined ? { 'content-type': 'application/json' } : {}),
      },
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal !== undefined ? { signal: options.signal } : {}),
    });
  } catch (error) {
    // A network failure has no envelope, so synthesise one rather than letting a raw
    // TypeError reach a component that expects ApiError.
    if ((error as Error).name === 'AbortError') throw error;
    const offline = new ApiError(0, {
      error: {
        code: 'NETWORK_UNAVAILABLE',
        message: 'Could not reach the ServePod API. Check your connection and try again.',
        request_id: '',
        details: {},
      },
    });
    if (options.quiet !== true) notify.error('Connection lost', offline.message);
    throw offline;
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text.length > 0 ? (JSON.parse(text) as unknown) : undefined;

  if (!response.ok) {
    const error = new ApiError(response.status, payload as ApiErrorBody);
    if (response.status === 401 && onUnauthenticated !== undefined) {
      onUnauthenticated();
    } else if (options.quiet !== true) {
      notify.error(titleFor(error), error.message);
    }
    throw error;
  }

  return payload as T;
}

/**
 * A short, human title for an error code.
 *
 * The envelope's message explains *what* went wrong; this supplies the heading a
 * person scans first. Codes without an entry fall back to a generic title rather than
 * showing the raw enum, which means nothing to a customer.
 */
function titleFor(error: ApiError): string {
  const titles: Record<string, string> = {
    CAPACITY_UNAVAILABLE: 'No capacity available',
    QUOTA_EXCEEDED: 'Quota reached',
    PAYMENT_REQUIRED: 'Payment required',
    SPENDING_LIMIT_EXCEEDED: 'Spending limit reached',
    ALREADY_EXISTS: 'Already exists',
    DUPLICATE_SSH_KEY: 'Key already added',
    INVALID_SSH_KEY: 'That key could not be read',
    PERMISSION_DENIED: 'Not permitted',
    ACCOUNT_SUSPENDED: 'Account suspended',
    VALIDATION_FAILED: 'Check the form',
    INVALID_STATE_TRANSITION: 'Not possible right now',
    RATE_LIMITED: 'Slow down',
    NOT_FOUND: 'Not found',
  };
  return titles[error.code] ?? 'Something went wrong';
}

// ---------------------------------------------------------------------------
// Typed resources
// ---------------------------------------------------------------------------

export interface Pod {
  id: string;
  name: string;
  state: string;
  region: string;
  template: string;
  offering: string;
  accelerator_count: number;
  vendor: 'NVIDIA' | 'AMD' | null;
  cpu_cores: number;
  memory_gb: number;
  disk_gb: number;
  purchase_mode: string;
  created_at: string;
  started_at: string | null;
  stopped_at: string | null;
  ssh_host: string | null;
  ssh_port: number | null;
  private_ip: string | null;
  failure_code: string | null;
  failure_message: string | null;
  startup_seconds: number | null;
  generation: number;
}

export interface Offering {
  slug: string;
  vendor: 'NVIDIA' | 'AMD';
  model: string;
  displayName: string;
  architecture: string;
  memoryGb: number;
  performanceClass: number;
  defaultCpuCores: number;
  defaultMemoryGb: number;
  defaultDiskGb: number;
  available: number;
  total: number;
  pricePerHour: string | null;
  currency: string;
  regions: { region: string; available: number }[];
}

export interface Template {
  slug: string;
  name: string;
  summary: string;
  description: string | null;
  category: string;
  official: boolean;
  icon: string | null;
  variants: {
    vendor: 'NVIDIA' | 'AMD';
    computeStack: 'CUDA' | 'ROCM';
    computeStackVersion: string;
    imageRepository: string;
    imageSizeBytes: number | null;
  }[];
  exposedPorts: { port: number; name: string; protocol: string }[];
}

export interface SshKey {
  id: string;
  name: string;
  fingerprint: string;
  type: string;
  bits: number;
  comment: string | null;
  created_at: string;
}

export interface Me {
  user: {
    id: string | null;
    email: string | null;
    display_name: string | null;
    email_verified: boolean;
    created_at: string | null;
  };
  account: { id: string; name: string | null; status: string; suspended: boolean };
  organizations: { id: string; name: string; slug: string; roles: string[] }[];
  memberships: { organizationId: string; roles: string[] }[];
  platform_roles: string[];
  is_staff: boolean;
  /** Null for members without billing.read — the shell hides the money card. */
  billing: {
    /** The live prepaid balance — negative when usage has outrun the ledger. */
    balance: string;
    credit: string;
    burn_per_hour: string;
    spend_this_month: string;
    payment_brand: string | null;
    payment_last4: string | null;
  } | null;
  onboarding: {
    has_ssh_key: boolean;
    has_pod: boolean;
    has_running_pod: boolean;
    complete: boolean;
  };
}

export interface Volume {
  id: string;
  name: string;
  size_gb: number;
  access_mode: 'RW_ONCE' | 'RO_MANY' | 'RW_MANY';
  state: string;
  region: string;
  attached_pods: number;
  created_at?: string;
}

export interface BillingBalance {
  currency: string;
  balance: string;
  components: {
    credit_available: string;
    invoiced_unpaid: string;
    uninvoiced_usage: string;
    open_run_accrual: string;
    /** Signed: positive granted, negative clawed back. */
    adjustments: string;
  };
  burn_per_hour: string;
  /** The policy that acts on this balance — shown WITH it, never discovered. */
  enforcement: {
    state: 'NONE' | 'WARNED' | 'FLOOR_BREACHED' | 'DISPOSED';
    floor: string;
    grace_hours: number;
    warn_below: string;
    floor_breached_at: string | null;
  };
  auto_reload: {
    enabled: boolean;
    threshold: string | null;
    amount: string | null;
    failures: number;
    disabled_reason: string | null;
  };
  /** The USD truth converted for display; null when the display currency is USD. */
  display: {
    currency: string;
    balance: string;
    burn_per_hour: string;
    fx_rate: string;
    rate_source: string;
  } | null;
  currencies: { preferred: string; supported: string[] };
  as_of: string;
}

export const resources = {
  me: () => api<Me>('/v1/me'),

  /** The display name is the only editable profile field; quiet so the form shows errors. */
  updateProfile: (input: { display_name: string }) =>
    api<{ user: { id: string; display_name: string } }>('/v1/me', {
      method: 'PATCH',
      body: input,
      quiet: true,
    }),

  billingBalance: () => api<BillingBalance>('/v1/billing/balance'),

  /** Returns the hosted checkout URL; the browser is sent there to pay. */
  invoices: () => api<{ invoices: InvoiceSummary[] }>('/v1/billing/invoices'),
  invoice: (id: string) => api<InvoiceDetailResponse>(`/v1/billing/invoices/${id}`),
  payInvoice: (id: string) =>
    api<{ url: string }>(`/v1/billing/invoices/${id}/pay`, {
      method: 'POST',
      body: {
        success_url: `${window.location.origin}/billing?invoice=paid`,
        cancel_url: `${window.location.origin}/billing`,
      },
    }),
  billingProfile: () => api<{ profile: BillingProfile | null }>('/v1/billing/profile'),
  updateBillingProfile: (profile: BillingProfile) =>
    api<{ updated: boolean }>('/v1/billing/profile', { method: 'PUT', body: profile }),

  topUp: (amount: number, currency: string) =>
    api<{ url: string }>('/v1/billing/top-up', {
      method: 'POST',
      body: {
        amount,
        currency,
        success_url: `${window.location.origin}/billing?topup=success`,
        cancel_url: `${window.location.origin}/billing`,
      },
    }),

  setPreferredCurrency: (currency: string) =>
    api<{ currency: string }>('/v1/billing/preferences', { method: 'PUT', body: { currency } }),

  updateAutoReload: (input: { enabled: boolean; threshold_usd: number; amount_usd: number }) =>
    api<{ enabled: boolean; threshold: string; amount: string }>('/v1/billing/auto-reload', {
      method: 'PUT',
      body: input,
    }),

  /** Returns the hosted card-capture URL. */
  addPaymentMethod: () =>
    api<{ url: string }>('/v1/billing/payment-method', {
      method: 'POST',
      body: {
        success_url: `${window.location.origin}/billing?card=saved`,
        cancel_url: `${window.location.origin}/billing`,
      },
    }),

  register: (input: {
    email: string;
    password: string;
    displayName?: string;
    organizationName?: string;
  }) =>
    api<{ account_id: string; user_id: string }>('/v1/auth/register', {
      method: 'POST',
      body: input,
      quiet: true,
    }),

  login: (input: { email: string; password: string }) =>
    api<{ user_id: string }>('/v1/auth/login', { method: 'POST', body: input, quiet: true }),

  logout: () => api<void>('/v1/auth/logout', { method: 'POST' }),

  offerings: () => api<{ offerings: Offering[] }>('/v1/catalog/offerings').then((r) => r.offerings),
  templates: () => api<{ templates: Template[] }>('/v1/catalog/templates').then((r) => r.templates),
  regions: () =>
    api<{ regions: { name: string; available: number; total: number; nodes: number }[] }>(
      '/v1/catalog/regions',
    ).then((r) => r.regions),

  pods: (includeDestroyed = false) =>
    api<{ pods: Pod[] }>(`/v1/pods${includeDestroyed ? '?include_destroyed=true' : ''}`).then(
      (r) => r.pods,
    ),
  pod: (id: string) => api<{ pod: Pod }>(`/v1/pods/${id}`).then((r) => r.pod),
  podHistory: (id: string) =>
    api<{
      history: {
        from: string | null;
        to: string;
        reason: string | null;
        actor: string;
        at: string;
      }[];
    }>(`/v1/pods/${id}/history`).then((r) => r.history),

  podLogs: (id: string) =>
    api<{
      snapshot: { content: string; captured_at: string } | null;
      pending: boolean;
    }>(`/v1/pods/${id}/logs`),
  refreshPodLogs: (id: string) =>
    api<{ requested: boolean }>(`/v1/pods/${id}/logs/refresh`, { method: 'POST' }),
  podGpu: (id: string) =>
    api<{
      samples: {
        device_id: string;
        recorded_at: string;
        utilisation_percent: number | null;
        memory_used_mb: number;
        memory_total_mb: number;
        temperature_celsius: number | null;
        power_watts: number | null;
      }[];
    }>(`/v1/pods/${id}/gpu`).then((r) => r.samples),

  createPod: (input: Record<string, unknown>) =>
    api<{ pod: Pod }>('/v1/pods', { method: 'POST', body: input, quiet: true }).then((r) => r.pod),
  stopPod: (id: string) =>
    api<{ pod: Pod }>(`/v1/pods/${id}/stop`, { method: 'POST' }).then((r) => r.pod),
  startPod: (id: string) =>
    api<{ pod: Pod }>(`/v1/pods/${id}/start`, { method: 'POST', quiet: true }).then((r) => r.pod),
  destroyPod: (id: string) =>
    api<{ pod: Pod }>(`/v1/pods/${id}`, { method: 'DELETE' }).then((r) => r.pod),
  volumes: () => api<{ volumes: Volume[] }>('/v1/volumes').then((r) => r.volumes),
  createVolume: (input: { name: string; size_gb: number; access_mode?: string }) =>
    api<{ volume: Volume }>('/v1/volumes', { method: 'POST', body: input, quiet: true }).then(
      (r) => r.volume,
    ),
  deleteVolume: (id: string) =>
    api<{ deleted: boolean }>(`/v1/volumes/${id}`, { method: 'DELETE', quiet: true }),

  sshKeys: () => api<{ ssh_keys: SshKey[] }>('/v1/ssh-keys').then((r) => r.ssh_keys),
  addSshKey: (input: { name: string; publicKey: string }) =>
    api<SshKey & { warning: string | null }>('/v1/ssh-keys', {
      method: 'POST',
      body: input,
      quiet: true,
    }),
  deleteSshKey: (id: string) => api<void>(`/v1/ssh-keys/${id}`, { method: 'DELETE' }),
};

export interface InvoiceSummary {
  id: string;
  number: string;
  status: 'DRAFT' | 'FINALIZED' | 'PAID' | 'UNCOLLECTIBLE' | 'VOID';
  subtotal: string;
  credit_applied: string;
  total: string;
  period_start: string;
  period_end: string;
  paid_at: string | null;
}

export interface InvoiceDetailResponse {
  invoice: InvoiceSummary & {
    currency: string;
    payment_reference: string | null;
    tax_note: string;
  };
  lines: {
    description: string;
    meter: string;
    quantity: string;
    unit_price: string;
    amount: string;
  }[];
  billed_to: BillingProfile | null;
}

export interface BillingProfile {
  company_name?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  postal_code?: string | null;
  country?: string | null;
  tax_id?: string | null;
}
